import moment from 'moment-timezone';
import { Lilate, LilateStartRule, LilateStartRuleSource } from '../model/lilate.model';
import { formatParisDay, isBeforeParisDay, parisToday, toParisDay } from './paris-day.util';

/**
 * Date de début du passage (O) et fenêtre de passage d'un LILATE.
 *
 * Engage calcule O (`earliest_start_date`) et la date butoir B (`expired_date`) : le front
 * ne refait aucun calcul de règle, il compare seulement au jour de Paris. En examinateur,
 * le candidat réserve dès l'inscription un créneau du jour O au jour B ; en IA, le test
 * ne démarre pas avant O.
 */

type LilateWindow = Pick<Lilate, 'earliest_start_date' | 'expired_date'>;

type LilateStartBasis = Pick<Lilate, 'start_from' | 'in_training' | 'lilate_start_rule'>;

/** True tant que la date de début O n'est pas atteinte (jour de Paris). */
export function isBeforeLilateStart(
    lilate: Pick<Lilate, 'earliest_start_date'> | null | undefined,
    now: Date | moment.Moment = new Date(),
): boolean {
    return isBeforeParisDay(lilate?.earliest_start_date, now);
}

/** Date de début O au format court (JJ/MM) des messages candidat. */
export function lilateStartShortLabel(lilate: Pick<Lilate, 'earliest_start_date'> | null | undefined): string {
    return formatParisDay(lilate?.earliest_start_date, 'DD/MM');
}

/**
 * Mois d'ouverture du calendrier examinateur : aujourd'hui, reporté à la date
 * de début O si elle est future, sans dépasser le mois de la date butoir B.
 */
export function getInitialLilateCalendarDate(
    lilate: LilateWindow | null | undefined,
    now: Date | moment.Moment = new Date(),
): moment.Moment {
    let start = moment(now);
    const startDay = toParisDay(lilate?.earliest_start_date);
    if (startDay && startDay > parisToday(now)) {
        start = moment(startDay, 'YYYY-MM-DD');
    }
    const endDay = toParisDay(lilate?.expired_date);
    if (endDay) {
        const end = moment(endDay, 'YYYY-MM-DD');
        if (start.isAfter(end, 'month')) {
            start = end;
        }
    }
    return start;
}

/** Libellé staff d'une règle de début (vide si aucune règle). */
export function lilateStartRuleLabel(
    rule: LilateStartRule | null | undefined,
    days?: number | null,
): string {
    switch (rule) {
        case 'end_of_training':
            return 'À la fin de la formation';
        case 'on_creation':
            return 'Dès l\'inscription';
        case 'days_before_end':
            return days === 1
                ? '1 jour avant la fin de la formation'
                : `${days ?? 'X'} jours avant la fin de la formation`;
        default:
            return '';
    }
}

/** Libellé staff de l'origine de la règle (vide si inconnue). */
export function lilateStartRuleSourceLabel(source: LilateStartRuleSource | null | undefined): string {
    switch (source) {
        case 'organization':
            return 'organisme';
        case 'entreprise':
            return 'entreprise';
        default:
            return '';
    }
}

/**
 * Ce qui fixe la date de début, dans l'ordre de calcul d'Engage :
 * date fixée pour ce LILATE, inscription hors formation, puis règle copiée.
 */
export function lilateStartDateBasisLabel(lilate: LilateStartBasis | null | undefined): string {
    if (lilate?.start_from) {
        return 'date fixée pour ce candidat';
    }
    if (!lilate?.in_training) {
        return 'hors formation : dès l\'inscription';
    }
    if (lilate.lilate_start_rule) {
        return 'règle de l\'inscription';
    }
    return 'calcul Engage, LILATE créé hors Partenaire';
}
